import { yupResolver } from "@hookform/resolvers/yup";
import React, { FC, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSearchClientSavedFiltersMutation } from "../../api/services/client/clientApis";
import {
  MenuListParams,
  SaveSearchFilterParams,
} from "../../api/services/client/models";
import {
  useGetLineOfBusiness,
  useGetProgramFrameWork,
  useSearchProgramTemplateMutation,
} from "../../api/services/programTemplate/programTemplateApis";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-icon.svg";
import { MuiBox, MuiMenuList } from "../../components";
import { CommonListModal } from "../../components/commonListModal/CommonListModal";
import { DeleteFilterModal } from "../../components/searchFilters/deleteFilter/DeleteFilter";
import { DeleteMultipleFilterModal } from "../../components/searchFilters/deleteMultipleFilters/DeleteMultipleFilters";
import { OverwriteFilterModal } from "../../components/searchFilters/overwriteFilter/OverwriteFilter";
import { SaveFilterModal } from "../../components/searchFilters/saveFilter/SaveFilter";
import {
  LANGUAGE_CODE,
  MAX_SAVE_FILTERS_LIMIT,
  MDM_ACTIVITY_TYPE_KEY,
  PAGE_IDS,
} from "../../helpers/constants";
import {
  isNotEmpty,
  mapFiltersToFormValues,
  trimValue,
} from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { FilterProps } from "../clientSearch/modelsAndSchemas/Models";
import { ProgramTemplateSearchForm } from "./components/programTemplateSearchForm/ProgramTemplateSearchForm";
import { ProgramTemplateSearchHeader } from "./components/programTemplateSearchHeader/ProgramTemplateSearchHeader";
import { ProgramTemplateSearchResult } from "./components/programTemplateSearchResult/ProgramTemplateSearchResult";
import {
  OptionListType,
  ProgramArrayList,
  ProgramTemplateSearchFormKey,
  ProgramTemplateSearchFormProps,
  SearchProgramTemplateResultRowValue,
} from "./modelsAndSchemas/Models";
import { SearchProgramTemplateSchema } from "./modelsAndSchemas/ValidationSchema";
import { programTemplateSiteCoreKeys } from "./siteCore/SiteCoreKeys";
import { useEnterKeyAction } from "../../hooks/useEnterKeyAction";

export const pageId = PAGE_IDS.PROGRAM_TEMPLATE;

interface ProgramTemplateSearchProps {
  showAsModal?: boolean;
}

const ProgramTemplateSearch: FC<ProgramTemplateSearchProps> = ({
  showAsModal = false,
}) => {
  const { isMobile } = useMediaQueries();
  const dispatch = useDispatch();
  const { commonValidationMessages, commonLabels } = useSiteCore();

  const countryRef = useRef<{ resetValues: (values?: string[]) => void }>(null);

  const [isSearchClick, setIsSearchClick] = useState(false);
  const [isOpenSaveFilterModal, setIsOpenSaveFilterModal] = useState(false);
  const [lineOfBusinessList, setLineOfBusinessList] = useState<OptionListType>(
    [],
  );
  const [programFrameWorkList, setProgramFrameWorkList] =
    useState<OptionListType>([]);

  const { data: lineOfBusinessData, isError: isLineOfBusinessError } =
    useGetLineOfBusiness(LANGUAGE_CODE.EN_US);
  const { data: programFrameWorkData, isError: isProgramFrameWorkError } =
    useGetProgramFrameWork(LANGUAGE_CODE.EN_US, MDM_ACTIVITY_TYPE_KEY.program);

  const [showModal, setShowModal] = useState(false);
  const [showSavedFilter, setShowSavedFilter] = useState(false);
  const [filtersData, setFiltersData] = useState<FilterProps[]>([]);
  const [filterToDelete, setFilterToDelete] = useState<MenuListParams>({
    id: "",
    name: "",
  });
  const [preloadFilter, setPreloadFilter] = useState(false);
  const [showMultipleDeleteModal, setShowMultipleDeleteModal] = useState(false);
  const [isMaxFilterReached, setIsMaxFilterReached] = useState(false);
  const [
    preloadFilterValuesProgramTemplate,
    setPreloadFilterValuesProgramTemplate,
  ] = useState({
    id: "",
    name: "",
    filters: filtersData,
  });

  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const defaultValues = {
    [ProgramTemplateSearchFormKey.programTemplateName]: "",
    [ProgramTemplateSearchFormKey.programTemplateNameFilter]:
      programTemplateSiteCoreKeys.contains,
    [ProgramTemplateSearchFormKey.lineOfBusiness]: "",
    [ProgramTemplateSearchFormKey.programFrameWork]: "",
    [ProgramTemplateSearchFormKey.status]: "",
    [ProgramTemplateSearchFormKey.country]: [],
  };

  const {
    mutateAsync: getProgramTemplateList,
    isPending,
    data: searchResult, // search result will be pass to result component
    isError: isProgramTemplateError,
  } = useSearchProgramTemplateMutation();

  const {
    data: listOfFilters,
    refetch: refetchListOfFilters,
    isError: isListOfFiltersError,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "ProgramTemplateSearch",
  });

  useEffect(() => {
    if (
      isLineOfBusinessError ||
      isProgramFrameWorkError ||
      isProgramTemplateError ||
      isListOfFiltersError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isLineOfBusinessError,
    isProgramFrameWorkError,
    isProgramTemplateError,
    isListOfFiltersError,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  const setDropDownOption = (data: ProgramArrayList[]) => {
    return Array.isArray(data)
      ? data.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      : [];
  };

  useEffect(() => {
    if (isNotEmpty(lineOfBusinessData)) {
      setLineOfBusinessList(setDropDownOption(lineOfBusinessData));
    }
    if (isNotEmpty(programFrameWorkData)) {
      setProgramFrameWorkList(setDropDownOption(programFrameWorkData));
    }
  }, [lineOfBusinessData, programFrameWorkData]);

  useEffect(() => {
    handleSearchClick();
  }, []);

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
    handleSubmit,
  } = useForm<ProgramTemplateSearchFormProps>({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(SearchProgramTemplateSchema(commonValidationMessages)),
  });

  const handleSearchClick = async () => {
    setIsSearchClick(true);
    const {
      programTemplateName,
      programTemplateNameFilter,
      lineOfBusiness,
      programFrameWork,
      status,
      country,
    } = getValues();
    const params = {
      name: trimValue(programTemplateName ?? ""),
      nameFilter: programTemplateNameFilter,
      lineOfBusinessId: lineOfBusiness,
      programFrameWorkId: programFrameWork,
      countryIds: country,
      status: status,
    };
    getProgramTemplateList(params);
  };

  const formValues = useWatch({ control });

  // While disabling the save btn - empty form - Fields to ignore in the check
  const ignoredFields = [
    ProgramTemplateSearchFormKey.programTemplateNameFilter,
  ] as string[];

  // While disabling the save btn - empty form - Check if any of the fields have values in them
  const isValidForm = Object.entries(formValues).some(
    ([key, value]) =>
      !ignoredFields.includes(key) &&
      (typeof value === "string" ? value.trim() !== "" : value.length > 0),
  );

  const handleClearAllSearch = () => {
    reset(defaultValues);
    removeSavedFilters();
    if (countryRef.current) {
      countryRef.current.resetValues();
    }
    handleSearchClick();
  };

  const removeSavedFilters = () => {
    setPreloadFilterValuesProgramTemplate({ id: "", name: "", filters: [] });
    setPreloadFilter(false);
  };

  const handleSetFilterModal = () => {
    setIsOpenSaveFilterModal(!isOpenSaveFilterModal);
  };

  const handleFilterRowClickProgramTemplate = (
    item: MenuListParams | SaveSearchFilterParams,
  ) => {
    setPreloadFilter(true);
    const itemFilterProgramTemplate = item as SaveSearchFilterParams;
    // if there is no value for country and previously some data exists, reset the country field
    countryRef?.current?.resetValues([]);
    itemFilterProgramTemplate.filters.forEach((row) => {
      if (row.fieldName === ProgramTemplateSearchFormKey.country) {
        // if the filter is country, split the value and convert it to array
        if (typeof row.fieldValue === "string") {
          row.fieldValue = row.fieldValue.split("|");
        }

        if (countryRef.current) {
          countryRef.current.resetValues(row.fieldValue);
        }
      }
    });

    setPreloadFilterValuesProgramTemplate({
      id: itemFilterProgramTemplate.id ?? "",
      name: itemFilterProgramTemplate.name ?? "",
      filters: mapFiltersToFormValues(
        itemFilterProgramTemplate.filters,
        defaultValues,
        ProgramTemplateSearchFormKey,
      ),
    });
    reset(
      mapFiltersToFormValues(
        itemFilterProgramTemplate.filters,
        defaultValues,
        ProgramTemplateSearchFormKey,
      ),
    );
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: MenuListParams,
  ) => {
    event.stopPropagation();
    if (preloadFilterValuesProgramTemplate.name === item.name) {
      removeSavedFilters();
    }

    setFilterToDelete({ id: item?.id, name: item?.name });
    if (showModal === false) {
      setShowModal(true);
    }
  };

  const handleSaveFilterModal = () => {
    if (
      listOfFilters &&
      listOfFilters.length >= MAX_SAVE_FILTERS_LIMIT &&
      !preloadFilter
    ) {
      if (showMultipleDeleteModal === false) {
        setShowMultipleDeleteModal(true);
      }
    } else {
      handleSetFilterModal();
    }
    const values = getValues();

    const filters: {
      fieldName: keyof ProgramTemplateSearchFormProps;
      operatorIdKey?: keyof ProgramTemplateSearchFormProps;
    }[] = [
      {
        fieldName: ProgramTemplateSearchFormKey.programTemplateName,
        operatorIdKey: ProgramTemplateSearchFormKey.programTemplateNameFilter,
      },
      { fieldName: ProgramTemplateSearchFormKey.lineOfBusiness },
      { fieldName: ProgramTemplateSearchFormKey.programFrameWork },
      { fieldName: ProgramTemplateSearchFormKey.status },
      { fieldName: ProgramTemplateSearchFormKey.country }, // Handle country as a concatenated string
    ];

    const data: FilterProps[] = filters
      .filter(({ fieldName }) => {
        // Special case for country field
        if (fieldName === ProgramTemplateSearchFormKey.country) {
          return (
            Array.isArray(values[fieldName]) && values[fieldName].length > 0
          );
        }
        return isNotEmpty(values[fieldName]?.toString().trim());
      })
      .map(({ fieldName, operatorIdKey }) => {
        if (fieldName === ProgramTemplateSearchFormKey.country) {
          // Concatenate country array values into a single string
          const concatenatedCountries = values[fieldName].join("|");
          return {
            fieldName: fieldName as string,
            fieldValue: concatenatedCountries,
            operatorId: "",
            savedSearchFilterDataType: 0,
          };
        }

        // Handle other fields
        return {
          fieldName: fieldName as string,
          fieldValue: trimValue(values[fieldName]?.toString()),
          operatorId: operatorIdKey ? values[operatorIdKey]?.toString() : "",
          savedSearchFilterDataType: 0,
        };
      });

    if (preloadFilterValuesProgramTemplate?.id) {
      setPreloadFilterValuesProgramTemplate({
        id: preloadFilterValuesProgramTemplate.id,
        name: preloadFilterValuesProgramTemplate.name,
        filters: data,
      });
    } else {
      setFiltersData(data);
    }
  };

  const handleSavedFilter = () => {
    if (showSavedFilter === false) {
      setShowSavedFilter(true);
    }
  };

  const closeSaveFilterModal = () => {
    setIsOpenSaveFilterModal(false);
  };

  const handleClose = () => {
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const handleModalClose = () => {
    if (showModal) {
      setShowModal(false);
    }
  };

  const handlePrimaryButtonClick = () => {
    setShowModal(false);
  };

  const handleMultipleDeletePrimaryButtonClick = () => {
    setShowMultipleDeleteModal(false);
  };

  const handleMultipleDeleteModalClose = () => {
    if (showMultipleDeleteModal) {
      setShowMultipleDeleteModal(false);

      setShowSavedFilter(false);
    }
  };

  const onSaveAsNewFilterClick = () => {
    setFiltersData([...preloadFilterValuesProgramTemplate.filters]);
    if (listOfFilters && listOfFilters.length >= MAX_SAVE_FILTERS_LIMIT) {
      setShowMultipleDeleteModal(true);
      setIsMaxFilterReached(true);
    } else {
      setPreloadFilter(false);
    }
  };

  const callbackAction = () => {
    if (isMaxFilterReached) {
      setPreloadFilter(false);
    }
    setIsOpenSaveFilterModal(true);
  };

  useEnterKeyAction({
    onEnter: handleSubmit(() => handleSearchClick()),
  });

  return (
    <MuiBox data-testid="program-template">
      <ProgramTemplateSearchHeader pageId={pageId} />

      <ProgramTemplateSearchForm
        control={control}
        handleSearchClick={handleSearchClick}
        handleClearAllSearch={handleClearAllSearch}
        handleSaveFilter={handleSaveFilterModal}
        pageId={pageId}
        programFrameList={programFrameWorkList}
        lineOfBusinessList={lineOfBusinessList}
        disabledSearch={!isValid}
        countryRef={countryRef}
        showAsModal={showAsModal}
        refetchListOfFilters={refetchListOfFilters}
        handleSavedFilter={handleSavedFilter}
        anchorRef={anchorRef}
        disableActionBtn={!listOfFilters?.length}
        isValidForm={isValidForm}
      />

      <ProgramTemplateSearchResult
        rows={
          (searchResult?.data as SearchProgramTemplateResultRowValue[]) || []
        }
        isSearch={isSearchClick}
        pageId={pageId}
      />

      {isOpenSaveFilterModal && !preloadFilter && (
        <SaveFilterModal
          showModal={isOpenSaveFilterModal}
          handleModalClose={closeSaveFilterModal}
          pageId={pageId}
          savedSearchPageName={1}
          filters={filtersData}
          listOfFilters={listOfFilters}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}

      {showSavedFilter &&
        listOfFilters &&
        (!isMobile ? (
          <MuiMenuList
            menuArray={listOfFilters}
            refValue={anchorRef.current}
            open={showSavedFilter}
            onClose={handleClose}
            onRowClick={handleFilterRowClickProgramTemplate}
            onActionBtnClick={handleDeleteClick}
          />
        ) : (
          <CommonListModal
            title={commonLabels.savedFilter}
            open={showSavedFilter}
            onIconButtonClick={handleDeleteClick}
            onFilterClick={handleFilterRowClickProgramTemplate}
            buttonIcon={<DeleteIcon />}
            listItems={listOfFilters}
            closeHandler={handleClose}
            secondaryButtonTitle={commonLabels.close}
          />
        ))}

      {showModal && (
        <DeleteFilterModal
          showModal={showModal}
          handleModalClose={handleModalClose}
          handlePrimaryButtonClick={handlePrimaryButtonClick}
          filterToDelete={filterToDelete}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}

      {showMultipleDeleteModal && (
        <DeleteMultipleFilterModal
          showModal={showMultipleDeleteModal}
          handleModalClose={handleMultipleDeleteModalClose}
          handlePrimaryButtonClick={handleMultipleDeletePrimaryButtonClick}
          listOfFilters={listOfFilters}
          refetchListOfFilters={refetchListOfFilters}
          callbackAction={callbackAction}
        />
      )}

      {preloadFilter && isOpenSaveFilterModal && (
        <OverwriteFilterModal
          showModal={isOpenSaveFilterModal && preloadFilter}
          handleModalClose={closeSaveFilterModal}
          handleSaveNewFilterClick={onSaveAsNewFilterClick}
          overrideFilterValues={preloadFilterValuesProgramTemplate}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}
    </MuiBox>
  );
};

export default ProgramTemplateSearch;
